import { useApiQuery, useApiMutation, queryKeys, mutateData } from '../../../api/client';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// Types
export type RecurringType = 'SUBSCRIPTION' | 'BILL' | 'INCOME' | 'LOAN_PAYMENT' | 'SPLITTED_PAYMENT';

export type Frequency =
  | 'DAILY'
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'SEMI_ANNUAL'
  | 'ANNUAL';

export interface RecurringEntity {
  id: string;
  type: RecurringType;
  name: string;
  payee?: string;
  amount: number;
  currency: 'SEK' | 'EUR';
  frequency: Frequency;
  categoryId: string;
  category?: {
    id: string;
    name: string;
    color?: string;
  };
  nextDueDate: string;
  lastOccurrence?: string;
  isActive: boolean;
  endDate?: string;
  reminderDays: number;
  notes?: string;
  // BILL-specific
  dueDay?: number;
  autoPay?: boolean;
  // INCOME-specific
  grossAmount?: number;
  taxWithheld?: number;
  // SPLITTED_PAYMENT-specific
  totalInstallments?: number;
  interestRate?: number;
  totalAmountWithInterest?: number;
  // Computed
  confirmedPayments?: number;
  lastPaymentAmount?: number;
  accountId?: string;
  accountName?: string;
  createdAt: string;
  updatedAt: string;
  merchant?: {
    id: string;
    name: string;
    logo: string | null;
  } | null;
}

export interface DetectedSubscription {
  name: string;
  amount: number;
  frequency: string;
  startDate: string;
  nextPaymentDate: string;
  categoryId?: string;
  transactionPattern: string;
  autoDetected: boolean;
  relatedTransactionIds: string[];
  occurrenceCount: number;
  averageIntervalDays: number;
  confidenceScore: number;
}

export interface RecurringSummary {
  totalMonthlyExpenses: number;
  totalMonthlyIncome: number;
  totalAnnualExpenses: number;
  totalAnnualIncome: number;
  net: number;
  activeCount: number;
  subscriptionCount: number;
  billCount: number;
  incomeCount: number;
  loanPaymentCount: number;
  splittedPaymentCount?: number;
}

export interface UpcomingPayment {
  id: string;
  name: string;
  type: RecurringType;
  amount: number;
  currency: string;
  frequency: Frequency;
  nextDueDate: string;
  daysUntilDue: number;
  isActive: boolean;
}

export interface CashFlowMonth {
  month: string;
  inflows: number;
  outflows: number;
  net: number;
  details: {
    entityId: string;
    name: string;
    type: RecurringType;
    amount: number;
  }[];
}

export interface CreateRecurringEntityInput {
  type: RecurringType;
  name: string;
  payee?: string;
  expectedAmount?: number;
  amount?: number;
  currency: 'SEK' | 'EUR';
  frequency: Frequency;
  categoryId: string;
  nextDueDate: string;
  startDate?: string;
  reminderDays?: number;
  notes?: string;
  // BILL-specific
  dueDay?: number;
  autoPay?: boolean;
  // INCOME-specific
  grossAmount?: number;
  taxWithheld?: number;
  // SPLITTED_PAYMENT-specific
  totalInstallments?: number;
  interestRate?: number;
  totalAmountWithInterest?: number;
}

export interface UpdateRecurringEntityInput extends Partial<CreateRecurringEntityInput> {
  isActive?: boolean;
}

export interface DetectSubscriptionsInput {
  startDate?: string;
  endDate?: string;
  minOccurrences?: number;
  toleranceDays?: number;
}

/**
 * Fetch all recurring entities, optionally filtered by isActive and/or type
 */
export function useRecurringEntities(filters?: { isActive?: boolean; type?: RecurringType }) {
  const params = new URLSearchParams();
  if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
  if (filters?.type) params.set('type', filters.type);
  const queryString = params.toString() ? `?${params.toString()}` : '';

  return useApiQuery<RecurringEntity[]>(
    queryKeys.recurring.list(filters as Record<string, unknown> || {}),
    `/api/recurring${queryString}`,
    {
      staleTime: 5 * 60 * 1000,
    }
  );
}

/**
 * Fetch a single recurring entity by ID
 */
export function useRecurringEntity(id: string) {
  return useApiQuery<RecurringEntity>(
    queryKeys.recurring.detail(id),
    `/api/recurring/${id}`,
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    }
  );
}

/**
 * Get recurring summary
 */
export function useRecurringSummary() {
  return useApiQuery<RecurringSummary>(
    ['recurring', 'summary'],
    '/api/recurring/summary',
    {
      staleTime: 5 * 60 * 1000,
    }
  );
}

/**
 * Get upcoming payments (next N days)
 */
export function useUpcomingPayments(days: number = 7) {
  return useApiQuery<UpcomingPayment[]>(
    ['recurring', 'upcoming', days],
    `/api/recurring/upcoming?days=${days}`,
    {
      staleTime: 2 * 60 * 1000,
    }
  );
}

/**
 * Get cash flow projection for N months.
 * The API returns { projections, summary } — we extract the array.
 */
export function useRecurringCashFlow(months: number = 6) {
  const query = useApiQuery<{ projections: CashFlowMonth[]; summary: unknown } | CashFlowMonth[]>(
    ['recurring', 'cash-flow', months],
    `/api/recurring/cash-flow?months=${months}`,
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const data: CashFlowMonth[] | undefined = Array.isArray(query.data)
    ? query.data
    : query.data?.projections;

  return { ...query, data };
}

/**
 * Get payment history for a recurring entity
 */
export function usePaymentHistory(entityId: string) {
  return useApiQuery<any[]>(
    ['recurring', entityId, 'payments'],
    `/api/recurring/${entityId}/payments`,
    {
      enabled: !!entityId,
      staleTime: 5 * 60 * 1000,
    }
  );
}

/**
 * Auto-detect subscriptions from transactions
 */
export function useDetectSubscriptions() {
  return useApiMutation<DetectedSubscription[], DetectSubscriptionsInput>(
    '/api/recurring/detect',
    'POST'
  );
}

/**
 * Create a new recurring entity
 */
export function useCreateRecurringEntity() {
  return useApiMutation<RecurringEntity, CreateRecurringEntityInput>(
    '/api/recurring',
    'POST',
    {
      invalidateKeys: [
        [...queryKeys.recurring.all],
        ['recurring', 'summary'],
      ],
    }
  );
}

/**
 * Update an existing recurring entity
 */
export function useUpdateRecurringEntity(id: string) {
  return useApiMutation<RecurringEntity, UpdateRecurringEntityInput>(
    `/api/recurring/${id}`,
    'PATCH',
    {
      invalidateKeys: [
        [...queryKeys.recurring.all],
        [...queryKeys.recurring.detail(id)],
        ['recurring', 'summary'],
      ],
    }
  );
}

/**
 * Update a recurring entity by ID (callable without knowing ID at hook time)
 */
export function useUpdateRecurringEntityById() {
  const queryClient = useQueryClient();

  return useMutation<RecurringEntity, AxiosError, { id: string } & UpdateRecurringEntityInput>({
    mutationFn: ({ id, ...data }) =>
      mutateData<RecurringEntity, UpdateRecurringEntityInput>(
        `/api/recurring/${id}`,
        'PATCH',
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recurring.all });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'summary'] });
    },
  });
}

/**
 * Delete a recurring entity
 */
export function useDeleteRecurringEntity() {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { id: string }>({
    mutationFn: ({ id }) =>
      mutateData<void, undefined>(`/api/recurring/${id}`, 'DELETE', undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recurring.all });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'summary'] });
    },
  });
}

// Proposed match types and hooks

export interface ProposedMatch {
  id: string;
  recurringEntityId: string;
  recurringEntityName: string;
  transactionId: string;
  expectedAmount: number;
  actualAmount: number;
  period: string;
  status: string;
  paidAt: string;
}

export interface ProposedPaymentDetail {
  paymentId: string;
  transactionId: string;
  expectedAmount: number;
  actualAmount: number;
  period: string;
  paidAt: string;
  transactionPayee: string | null;
  transactionDescription: string | null;
  transactionAmount: number | null;
  transactionDate: string | null;
  transactionType: string | null;
  installmentNumber?: number;
}

export interface ProposedEntityGroup {
  recurringEntityId: string;
  entityName: string;
  entityType: string;
  entityPayee: string | null;
  frequency: string;
  expectedAmount: number;
  currency: string;
  accountName: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  nextDueDate: string;
  isActive: boolean;
  confirmedPayments: number;
  totalPayments: number;
  proposedCount: number;
  proposals: ProposedPaymentDetail[];
}

interface ProposedResponse {
  grouped: ProposedEntityGroup[];
  flat: ProposedMatch[];
}

/**
 * Fetch all proposed payment matches (grouped by entity)
 */
export function useProposedMatchesGrouped() {
  return useApiQuery<ProposedResponse>(
    ['recurring', 'proposed'],
    '/api/recurring/proposed',
    { staleTime: 2 * 60 * 1000 }
  );
}

/**
 * Fetch all proposed payment matches (flat list, backward compatible)
 */
export function useProposedMatches() {
  const query = useProposedMatchesGrouped();
  return {
    ...query,
    data: query.data?.flat ?? [],
  };
}

/**
 * Confirm or reject a proposed payment match
 */
export function useConfirmPayment(entityId: string, paymentId: string) {
  return useApiMutation<any, { action: 'confirm' | 'reject' }>(
    `/api/recurring/${entityId}/payments/${paymentId}`,
    'PATCH',
    {
      invalidateKeys: [
        ['recurring', 'proposed'],
        [...queryKeys.recurring.all],
        ['recurring', entityId, 'payments'],
        ['recurring', 'summary'],
        ['recurring', 'cash-flow'],
        ['recurring', 'duplicates'],
        ['notifications'],
      ],
    }
  );
}

/**
 * Confirm or reject a proposed payment match (callable without knowing IDs at hook time)
 */
export interface ResolvePaymentInput {
  entityId: string;
  paymentId: string;
  action: 'confirm' | 'reject';
  type?: RecurringType;
  totalInstallments?: number;
  interestRate?: number;
  totalAmountWithInterest?: number;
}

export function useResolveProposedPayment() {
  const queryClient = useQueryClient();

  return useMutation<any, AxiosError, ResolvePaymentInput>({
    mutationFn: ({ entityId, paymentId, action, type, totalInstallments, interestRate, totalAmountWithInterest }) =>
      mutateData<any, Record<string, unknown>>(
        `/api/recurring/${entityId}/payments/${paymentId}`,
        'PATCH',
        { action, ...(type ? { type } : {}), ...(totalInstallments !== undefined ? { totalInstallments } : {}), ...(interestRate !== undefined ? { interestRate } : {}), ...(totalAmountWithInterest !== undefined ? { totalAmountWithInterest } : {}) }
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['recurring', 'proposed'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.recurring.all });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'cash-flow'] });
      queryClient.invalidateQueries({ queryKey: ['recurring', variables.entityId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'duplicates'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Bulk confirm or reject proposed payments across entities
 */
export interface BulkResolveAction {
  entityId: string;
  paymentId: string;
  action: 'confirm' | 'reject';
  type?: RecurringType;
  totalInstallments?: number;
  interestRate?: number;
  totalAmountWithInterest?: number;
}

export function useBulkResolveProposedPayments() {
  const queryClient = useQueryClient();

  return useMutation<
    { results: { paymentId: string; status: string }[] },
    AxiosError,
    { actions: BulkResolveAction[] }
  >({
    mutationFn: (body) =>
      mutateData<
        { results: { paymentId: string; status: string }[] },
        { actions: BulkResolveAction[] }
      >('/api/recurring/payments/bulk-resolve', 'POST', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring', 'proposed'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.recurring.all });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'cash-flow'] });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'duplicates'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Bulk link multiple transactions to a recurring entity
 */
export function useBulkLinkPayments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { entityId: string; transactionIds: string[] }) =>
      mutateData<
        { linked: number; skipped: number; results: { transactionId: string; paymentId?: string; status: string; reason?: string }[] },
        { entityId: string; transactionIds: string[] }
      >('/api/recurring/bulk-link-payments', 'POST', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recurring.all });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'proposed'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
      queryClient.invalidateQueries({ queryKey: ['recurring', 'summary'] });
    },
  });
}

/**
 * Link a transaction to a recurring entity
 */
export function useLinkPayment(entityId: string) {
  return useApiMutation<any, { transactionId: string }>(
    `/api/recurring/${entityId}/payments`,
    'POST',
    {
      invalidateKeys: [
        [...queryKeys.recurring.detail(entityId)],
        ['recurring', entityId, 'payments'],
        [...queryKeys.transactions.all],
      ],
    }
  );
}

// Amount change types and hooks

export interface AmountChange {
  id: string;
  previousAmount: number;
  newAmount: number;
  reason: string | null;
  effectiveDate: string;
  source: string; // 'manual' | 'auto_detected' | 'payment_mismatch'
  notes: string | null;
  createdAt: string;
}

export interface RecordAmountChangeInput {
  newAmount: number;
  reason?: string;
  effectiveDate?: string;
  notes?: string;
}

/**
 * Fetch amount change history for a recurring entity
 */
export function useAmountHistory(entityId: string) {
  return useApiQuery<AmountChange[]>(
    ['recurring', entityId, 'amount-history'],
    `/api/recurring/${entityId}/amount-history`,
    {
      enabled: !!entityId,
      staleTime: 5 * 60 * 1000,
    }
  );
}

/**
 * Record an amount change (salary raise, price change)
 */
export function useRecordAmountChange(entityId: string) {
  return useApiMutation<any, RecordAmountChangeInput>(
    `/api/recurring/${entityId}/amount-change`,
    'POST',
    {
      invalidateKeys: [
        ['recurring', entityId, 'amount-history'],
        [...queryKeys.recurring.detail(entityId)],
        [...queryKeys.recurring.all],
        ['recurring', 'summary'],
      ],
    }
  );
}

// Installment progress types and hooks

export interface InstallmentProgress {
  currentInstallment: number;
  totalInstallments: number;
  percentComplete: number;
  totalPaid: number;
  remainingAmount: number;
  totalAmountWithInterest: number;
  interestRate: number;
  principalTotal: number;
  totalInterest: number;
  principalPaid: number;
  interestPaid: number;
  estimatedCompletionDate: string | null;
}

/**
 * Fetch installment progress for a SPLITTED_PAYMENT entity
 */
export function useInstallmentProgress(entityId: string, enabled = true) {
  return useApiQuery<InstallmentProgress>(
    ['recurring', entityId, 'installment-progress'],
    `/api/recurring/${entityId}/installment-progress`,
    {
      enabled: !!entityId && enabled,
      staleTime: 2 * 60 * 1000,
    }
  );
}
